/* NPFC free Tend offer: basket reconciliation.
 *
 * Loaded only when the "Free Tend offer is live" theme setting is on (see
 * snippets/npfc-styles.liquid, which also defines window.NPFC_OFFER).
 *
 * Shopify enforces the price: the automatic discount "Free Tend 250ml with any
 * two" makes one Tend free when two units from the qualifying set are in the
 * basket. This file never sets a price. All it does is put the Tend line into the
 * basket for the shopper, and take it out again if the offer no longer applies.
 *
 * Rules, in the order they are checked on every pass:
 *  1. Qualifying units below the threshold: remove OUR gift line (the one carrying
 *     the hidden _npfc_gift property). A Tend the shopper added themselves is never
 *     touched.
 *  2. Our gift line exists: keep it at quantity 1, and check Shopify really has made
 *     it free. If it has not (discount missing, paused, misconfigured), remove it and
 *     switch the offer messaging off for this session. We never leave a paid "free" line.
 *  3. The shopper already has a Tend of their own: do nothing. The discount makes one
 *     unit free, so there is nothing to add.
 *  4. The shopper removed the gift on purpose: respect it until they drop below the
 *     threshold and come back.
 *  5. Otherwise add exactly one gift line.
 *
 * Every pass reads /cart.js first and only changes what is out of line, so running it
 * twice, or from several triggers at once, is safe. A busy flag stops overlapping
 * passes.
 *
 * QA only: sessionStorage npfc_offer_verify = 'off' skips the "is it really free"
 * check, so the basket states can be inspected before the discount exists.
 */
(() => {
  const cfg = window.NPFC_OFFER;
  if (!cfg || !cfg.live || window.NPFC_GIFT) return;

  const K = { off: 'npfc_offer_off', declined: 'npfc_gift_declined', had: 'npfc_gift_had', verify: 'npfc_offer_verify', seen: 'npfc_offer_seen' };
  const ss = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
    del(k) { try { sessionStorage.removeItem(k); } catch (e) {} }
  };
  const track = (name, params) => { if (typeof window.npfcTrack === 'function') window.npfcTrack(name, params || {}); };
  const once = (key, name, params) => {
    const seen = (ss.get(K.seen) || '').split(',');
    if (seen.includes(key)) return;
    seen.push(key);
    ss.set(K.seen, seen.join(','));
    track(name, params);
  };

  const qualifying = new Set((cfg.qualifyingVariants || []).map(Number));
  const isGiftLine = it => !!(it.properties && it.properties[cfg.prop] === cfg.propValue);
  const isTend = it => Number(it.variant_id) === Number(cfg.giftVariant);
  const units = cart => cart.items.reduce((n, it) => n + (qualifying.has(Number(it.variant_id)) ? it.quantity : 0), 0);
  const isFree = it => (it.discounts && it.discounts.length > 0) || Number(it.final_line_price) < Number(it.original_line_price);

  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(r => r.json());
  const sectionIds = () => Array.from(document.querySelectorAll('cart-items-component'))
    .map(c => c.dataset && c.dataset.sectionId).filter(Boolean).join(',');
  const post = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  }).then(r => r.json());

  // Tell Horizon's cart drawer / cart page to redraw from the sections we asked for.
  let eventsModule, selfDispatch = false;
  const events = () => eventsModule || (eventsModule = import('@shopify/events').catch(() => null));
  async function announce(action, cart, lines, sections) {
    const ev = await events();
    if (!ev || !ev.CartLinesUpdateEvent) return;
    const deferred = ev.CartLinesUpdateEvent.createPromise();
    selfDispatch = true;
    try { document.dispatchEvent(new ev.CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise })); }
    finally { selfDispatch = false; }
    deferred.resolve({
      cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'npfc-gift', sourceId: 'npfc-gift', itemCount: 1, sections, didError: false }
    });
  }

  async function addGift(how) {
    const res = await post((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add', {
      items: [{ id: Number(cfg.giftVariant), quantity: 1, properties: { [cfg.prop]: cfg.propValue } }],
      sections: sectionIds()
    });
    if (res.status) throw new Error(res.message || 'gift add failed');
    ss.set(K.had, '1');
    const cart = await getCart();
    await announce('add', cart, [{ merchandiseId: String(cfg.giftVariant), quantity: 1 }], res.sections);
    track('free_tend_added', { how });
    return cart;
  }

  async function changeLine(key, quantity, action, how) {
    const res = await post('/cart/change.js', { id: key, quantity, sections: sectionIds() });
    if (res.status) throw new Error(res.message || 'gift change failed');
    await announce(action, res, [{ merchandiseId: String(cfg.giftVariant), quantity }], res.sections);
    if (quantity === 0) track('free_tend_removed', { by: how });
    return res;
  }

  // Shopify did not make the gift free: take it out and stop making the promise this session.
  async function withdraw(giftLines) {
    for (const l of giftLines) await changeLine(l.key, 0, 'remove', 'unverified');
    ss.set(K.off, '1');
    ss.del(K.had);
    document.documentElement.classList.add('npfc-offer-off');
    track('free_tend_unverified');
  }

  let busy = false, again = false, timer = 0;
  async function reconcile() {
    if (busy) { again = true; return; }
    busy = true;
    try {
      const cart = await getCart();
      const n = units(cart);
      const eligible = n >= cfg.need;
      const giftLines = cart.items.filter(isGiftLine);
      const tendQty = cart.items.filter(isTend).reduce((sum, it) => sum + it.quantity, 0);

      if (n === 1) once('one', 'free_tend_one_away');

      if (!eligible) {
        ss.del(K.declined); ss.del(K.had);
        for (const l of giftLines) await changeLine(l.key, 0, 'remove', 'eligibility');
        return;
      }
      once('unlocked', 'free_tend_unlocked', { units: n });

      if (giftLines.length) {
        ss.set(K.had, '1');
        const g = giftLines[0];
        if (g.quantity > 1) { await changeLine(g.key, 1, 'update', 'clamp'); return; }
        if (ss.get(K.verify) !== 'off' && !isFree(g)) await withdraw(giftLines);
        return;
      }
      if (tendQty > 0) return;
      if (ss.get(K.had) === '1') { ss.set(K.declined, '1'); ss.del(K.had); track('free_tend_declined'); return; }
      if (ss.get(K.declined) === '1' || ss.get(K.off) === '1') return;

      const after = await addGift('auto');
      const g = after.items.find(isGiftLine);
      if (g && ss.get(K.verify) !== 'off' && !isFree(g)) await withdraw([g]);
    } catch (err) {
      // the basket must never break because of a gift
    } finally {
      busy = false;
      if (again) { again = false; schedule(400); }
      scanViews();
    }
  }
  function schedule(ms) { clearTimeout(timer); timer = setTimeout(reconcile, ms == null ? 250 : ms); }

  // one impression per surface per session
  let io;
  function scanViews() {
    if (!('IntersectionObserver' in window)) return;
    io = io || new IntersectionObserver(entries => entries.forEach(e => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      once('view-' + e.target.dataset.surface, 'free_tend_offer_view', { surface: e.target.dataset.surface });
    }), { threshold: 0.5 });
    document.querySelectorAll('[data-npfc-offer]:not([data-npfc-seen])').forEach(el => { el.dataset.npfcSeen = '1'; io.observe(el); });
  }

  // "Add it to my basket" button in the cart (only shown when the gift is earned but not in the basket)
  document.addEventListener('click', e => {
    const b = e.target.closest && e.target.closest('[data-npfc-gift-add]');
    if (!b) return;
    e.preventDefault();
    ss.del(K.declined);
    b.disabled = true;
    addGift('button').catch(() => { b.disabled = false; });
  });

  // Any change to the basket, from Horizon's own components or from anything else that calls the cart endpoints
  events().then(ev => {
    if (!ev || !ev.StandardEvents) return;
    document.addEventListener(ev.StandardEvents.cartLinesUpdate, e => {
      if (selfDispatch) return;
      if (e.promise && e.promise.then) e.promise.then(() => schedule(350), () => {});
      else schedule(350);
    });
  });
  const nativeFetch = window.fetch;
  window.fetch = function (input) {
    const p = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) p.then(() => schedule(400), () => {});
    } catch (e) {}
    return p;
  };
  window.addEventListener('pageshow', e => { if (e.persisted) schedule(100); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') schedule(200); });
  document.addEventListener('shopify:section:load', scanViews);

  window.NPFC_GIFT = { reconcile, units };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { schedule(300); scanViews(); });
  else { schedule(300); scanViews(); }
})();
